// ─── The customer-facing booking journey ─────────────────────────────────────
//
// A branded, phone-first flow: choose a service, choose a time, confirm who
// you are, request it.
//
// ── What a customer must never see ──────────────────────────────────────────
//
// No internal business controls, and no sense of how busy the business is.
// Blocked and occupied time simply is not offered — it does not appear as
// "unavailable" with a reason attached, because why Carrie is unavailable on
// Thursday is not a customer's business.
//
// ── Why the button says what it says ────────────────────────────────────────
//
// Under approval booking the action is "Request appointment" and the result is
// "requested", because that is what actually happened. Calling it "Book" and
// showing a confirmation would be the system telling somebody they have an
// appointment they do not yet have — the precise failure this product exists
// to avoid, in the one place a customer would act on it.

import { useEffect, useMemo, useReducer, useState } from "react";
import { MonthCalendar, type MonthDay } from "./MonthCalendar.js";
import { buildMonthGrid } from "./monthGrid.js";
import {
  journeyReducer, initialJourney, detailsComplete, describeBooking,
  type JourneyState, type JourneyService, type JourneySlot, type JourneyDay,
  type CustomerDetails,
} from "./journey.js";

export type BookingJourneyProps = {
  services: JourneyService[];
  /**
   * The month currently on screen, for the chosen service.
   *
   * The parent asks the availability engine; this component never derives
   * availability itself. It groups and renders what it is given, and nothing
   * more — the design doc rules out two surfaces computing what is free.
   */
  daysFor: (service: JourneyService) => JourneyDay[];
  /** Free times on the chosen DAY. One day's worth, not a quarter's. */
  slotsFor: (service: JourneyService, day: Date) => JourneySlot[];
  /**
   * The customer moved to a different month, so the caller can fetch it.
   * `monthIndex` is 0-11, matching `Date`.
   */
  onMonthChange?: (service: JourneyService, year: number, monthIndex: number) => void;
  /** A day was chosen — the caller fetches that day's times. */
  onPickDay?: (service: JourneyService, day: Date) => void;
  /** True while the caller is fetching. Distinguishes "still loading" from
   *  "nothing free", which a customer must never see confused. */
  loading?: boolean;
  /** Carried in for a returning customer, so they are not asked twice. */
  knownCustomer?: CustomerDetails | null;
  /** Approval mode asks; instant mode books. Drives the wording, which must
   *  match what will actually happen. */
  mode?: "request" | "instant";
  onSubmit: (input: {
    service: JourneyService;
    slot: JourneySlot;
    details: CustomerDetails;
    clientActionId: string;
  }) => Promise<{ bookingId: string; status: "requested" | "confirmed" }>;
  /** Injectable so tests are deterministic; defaults to a real random id. */
  newActionId?: () => string;
  businessName?: string;
};

const money = (minor?: number | null, currency?: string | null) =>
  minor == null ? null : new Intl.NumberFormat("en-GB", { style: "currency", currency: currency ?? "GBP" }).format(minor / 100);

const timeOf = (d: Date) => d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", hour12: false });
const dayOf = (d: Date) => d.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" });

const panel: React.CSSProperties = {
  background: "var(--nl-surface)",
  border: "1px solid var(--nl-stroke)",
  borderRadius: "var(--nl-radius-card)",
  padding: 14,
};

const primaryButton: React.CSSProperties = {
  borderRadius: "var(--nl-radius-control)",
  border: "1px solid var(--nl-stroke)",
  background: "var(--nl-state-open)",
  color: "var(--nl-state-open-ink)",
  minHeight: "var(--nl-hit-min)",
  padding: "0 18px", font: "inherit", cursor: "pointer", width: "100%",
};

/** Is this calendar day before today? Compared on the date alone — a slot
 *  earlier TODAY is the availability engine's business, not the calendar's. */
function isPast(date: Date): boolean {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return date.getTime() < today.getTime();
}

const monthNav: React.CSSProperties = {
  minHeight: "var(--nl-hit-min)", minWidth: "var(--nl-hit-min)",
  borderRadius: "var(--nl-radius-control)", border: "1px solid var(--nl-stroke)",
  background: "var(--nl-surface-raised)", color: "var(--nl-ink)",
  font: "inherit", cursor: "pointer",
};

export function BookingJourney({
  services, daysFor, slotsFor, onMonthChange, onPickDay, loading = false,
  knownCustomer, mode = "request", onSubmit, newActionId, businessName,
}: BookingJourneyProps) {
  const [state, dispatch] = useReducer(journeyReducer, knownCustomer, initialJourney);

  /** The month on screen. Starts on the current one; the customer moves it. */
  const [viewMonth, setViewMonth] = useState(() => {
    const now = new Date();
    return { year: now.getFullYear(), monthIndex: now.getMonth() };
  });

  // Tell the caller which month to fetch, whenever it changes or the service
  // does. A two-hour colour has different free days from a half-hour trim, so
  // the month has to be re-asked per service rather than cached across them.
  useEffect(() => {
    if (state.service) onMonthChange?.(state.service, viewMonth.year, viewMonth.monthIndex);
  }, [state.service, viewMonth.year, viewMonth.monthIndex, onMonthChange]);

  const days = useMemo(
    () => (state.service ? daysFor(state.service) : []),
    [state.service, daysFor],
  );

  const slots = useMemo(
    () => (state.service && state.day ? slotsFor(state.service, state.day) : []),
    [state.service, state.day, slotsFor],
  );

  /**
   * The month grid, with each day's facts attached.
   *
   * Days the caller has no data for render as CLOSED rather than as free.
   * Getting that the wrong way round would offer a customer a day the salon
   * is shut, which they would only discover by turning up.
   */
  const monthDays: MonthDay[] = useMemo(() => {
    const facts = new Map(days.map(d => [d.date.toDateString(), d]));
    return buildMonthGrid(viewMonth.year, viewMonth.monthIndex).map(g => {
      const f = facts.get(g.date.toDateString());
      // A day that has gone reads as closed whatever the data says. The month
      // grid is padded with the previous month's tail, which is how the
      // calendar came to offer a day a fortnight in the past.
      const past = isPast(g.date);
      return {
        date: g.date,
        inMonth: g.inMonth,
        working: !past && (f?.working ?? false),
        openSlots: past ? 0 : f?.openSlots ?? 0,
        bookings: 0,
      };
    });
  }, [days, viewMonth]);

  const monthLabel = new Date(viewMonth.year, viewMonth.monthIndex, 1)
    .toLocaleDateString("en-GB", { month: "long", year: "numeric" });

  /** A month back or forward. Never before the current month — offering to
   *  browse into the past wastes taps on days nobody can book. */
  const now = new Date();
  const atEarliestMonth =
    viewMonth.year === now.getFullYear() && viewMonth.monthIndex === now.getMonth();
  const moveMonth = (delta: number) => {
    const d = new Date(viewMonth.year, viewMonth.monthIndex + delta, 1);
    setViewMonth({ year: d.getFullYear(), monthIndex: d.getMonth() });
  };

  async function submit() {
    if (state.submitting || !state.service || !state.slot || !state.details) return;
    // Created once per tap and kept across retries — see journey.ts. Reusing
    // it is what makes a dropped connection safe.
    const id = state.clientActionId ?? (newActionId?.() ?? crypto.randomUUID());
    dispatch({ type: "submit", clientActionId: id });
    try {
      const result = await onSubmit({
        service: state.service, slot: state.slot, details: state.details, clientActionId: id,
      });
      dispatch({ type: "submitted", ...result });
    } catch (err) {
      dispatch({
        type: "failed",
        error: err instanceof Error ? err.message : "That could not be sent. Please try again.",
      });
    }
  }

  return (
    <div
      data-nl-theme="nulife"
      data-testid="booking-journey"
      data-step={state.step}
      style={{ display: "flex", flexDirection: "column", gap: 14, maxWidth: 460, fontFamily: "var(--nl-font)" }}
    >
      <header>
        {businessName && (
          <div style={{ fontSize: "0.72rem", letterSpacing: "0.12em", textTransform: "uppercase", color: "var(--nl-ink-faint)" }}>
            {businessName}
          </div>
        )}
        <h1 style={{ margin: "4px 0 0", fontSize: "1.35rem", fontWeight: 600, color: "var(--nl-ink)" }}>
          {state.step === "done" ? "All done" : "Book an appointment"}
        </h1>
      </header>

      {/* ── Choose a service ───────────────────────────────────────────── */}
      {state.step === "service" && (
        <section style={panel} aria-label="Choose a service">
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {services.map(s => (
              <button
                key={s.id}
                type="button"
                data-testid="service-option"
                onClick={() => dispatch({ type: "pickService", service: s })}
                style={{
                  ...primaryButton,
                  background: "var(--nl-surface-raised)",
                  color: "var(--nl-ink)",
                  textAlign: "left", padding: "10px 14px", height: "auto", minHeight: "var(--nl-hit-min)",
                  display: "flex", flexDirection: "column", gap: 2, alignItems: "flex-start",
                }}
              >
                <span style={{ fontSize: "1rem" }}>{s.name}</span>
                <span style={{ fontSize: "0.8rem", color: "var(--nl-ink-dim)" }}>
                  {s.durationMinutes} minutes{money(s.priceMinor, s.currency) ? ` · ${money(s.priceMinor, s.currency)}` : ""}
                </span>
              </button>
            ))}
          </div>
        </section>
      )}

      {/* ── Choose a day ───────────────────────────────────────────────── */}
      {state.step === "day" && (
        <section style={panel} aria-label="Choose a day" data-testid="day-step">
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
            <button
              type="button"
              data-testid="month-back"
              onClick={() => moveMonth(-1)}
              disabled={atEarliestMonth}
              aria-label="Previous month"
              style={{ ...monthNav, opacity: atEarliestMonth ? 0.4 : 1 }}
            >←</button>
            <div style={{ flex: 1, textAlign: "center", color: "var(--nl-ink)", fontWeight: 600 }}>
              {monthLabel}
            </div>
            <button
              type="button"
              data-testid="month-forward"
              onClick={() => moveMonth(1)}
              aria-label="Next month"
              style={monthNav}
            >→</button>
          </div>

          <MonthCalendar
            days={monthDays}
            selected={state.day}
            // `customer` is not a detail. It swaps booking-volume dots for
            // availability dots and the label for one with no numbers in it —
            // how busy the salon is, is not a customer's business.
            surface="customer"
            density="compact"
            monthLabel={monthLabel}
            onSelect={date => {
              const facts = monthDays.find(d => d.date.toDateString() === date.toDateString());
              // A closed or full day is not selectable. Letting it through
              // would walk the customer to an empty list of times and leave
              // them wondering what they did wrong.
              if (!facts?.working || facts.openSlots === 0) return;
              // Nor a day that has already gone. The server also refuses these
              // — this is the structural half, so a calendar cannot offer the
              // past even if something upstream says it is free.
              if (isPast(date)) return;
              dispatch({ type: "pickDay", day: date });
              if (state.service) onPickDay?.(state.service, date);
            }}
          />

          {loading && (
            <p role="status" style={{ margin: "10px 0 0", color: "var(--nl-ink-dim)", fontSize: "0.85rem" }}>
              Loading…
            </p>
          )}
          {!loading && days.every(d => !d.working || d.openSlots === 0) && (
            <p style={{ margin: "10px 0 0", color: "var(--nl-ink-dim)", fontSize: "0.85rem" }}>
              Nothing free this month. Try the next one.
            </p>
          )}
        </section>
      )}

      {/* ── Choose a time ──────────────────────────────────────────────── */}
      {state.step === "slot" && (
        <section style={panel} aria-label="Choose a time">
          <div style={{ fontSize: "0.78rem", color: "var(--nl-ink-dim)", marginBottom: 8 }}>
            {state.day && dayOf(state.day)}
          </div>

          {/* Loading and empty are DIFFERENT, and shown differently. A
              customer told "no times" while the request is still in flight
              goes somewhere else. */}
          {loading ? (
            <p role="status" style={{ margin: 0, color: "var(--nl-ink-dim)", fontSize: "0.9rem" }}>
              Loading times…
            </p>
          ) : slots.length === 0 ? (
            <p style={{ margin: 0, color: "var(--nl-ink-dim)", fontSize: "0.9rem" }}>
              Nothing free that day. Go back and try another.
            </p>
          ) : (
            <div style={{ display: "flex", flexWrap: "wrap", gap: "var(--nl-tile-gap)" }}>
              {slots.map(slot => (
                <button
                  key={slot.startsAt.toISOString()}
                  type="button"
                  data-testid="slot-option"
                  onClick={() => dispatch({ type: "pickSlot", slot })}
                  style={{
                    borderRadius: "var(--nl-radius-chip)",
                    border: "1px solid var(--nl-stroke)",
                    background: "var(--nl-state-open)",
                    color: "var(--nl-state-open-ink)",
                    minHeight: "var(--nl-hit-min)",
                    padding: "0 14px", font: "inherit", cursor: "pointer",
                    fontVariantNumeric: "tabular-nums",
                  }}
                >{timeOf(slot.startsAt)}</button>
              ))}
            </div>
          )}
        </section>
      )}

      {/* ── Who you are ────────────────────────────────────────────────── */}
      {state.step === "details" && (
        <DetailsStep
          initial={state.details}
          onDone={details => dispatch({ type: "setDetails", details })}
        />
      )}

      {/* ── Review ─────────────────────────────────────────────────────── */}
      {state.step === "review" && (
        <section style={panel} aria-label="Check and confirm">
          <p style={{ margin: "0 0 10px", color: "var(--nl-ink)" }}>{describeBooking(state)}</p>
          <p style={{ margin: "0 0 14px", color: "var(--nl-ink-dim)", fontSize: "0.88rem" }}>
            {state.details?.fullName} · {state.details?.phone}
          </p>

          {state.error && (
            <p role="alert" data-testid="journey-error" style={{ color: "var(--nl-status-declined)", fontSize: "0.88rem", margin: "0 0 12px" }}>
              {state.error}
            </p>
          )}

          <button
            type="button"
            data-testid="journey-submit"
            disabled={state.submitting}
            onClick={submit}
            style={{ ...primaryButton, opacity: state.submitting ? 0.6 : 1 }}
          >
            {state.submitting
              ? "Sending…"
              : mode === "instant" ? "Book appointment" : "Request appointment"}
          </button>

          <p style={{ margin: "10px 0 0", fontSize: "0.78rem", color: "var(--nl-ink-faint)" }}>
            {mode === "instant"
              ? "Your appointment will be booked straight away."
              : "This sends a request. You will hear back once it is confirmed."}
          </p>
        </section>
      )}

      {/* ── Done ───────────────────────────────────────────────────────── */}
      {state.step === "done" && state.result && (
        <section style={panel} aria-label="Booking sent" data-testid="journey-done">
          <p style={{ margin: 0, color: "var(--nl-ink)" }}>
            {/* Says what actually happened. A request is not a confirmation,
                and telling somebody otherwise is the failure this product is
                built to avoid. */}
            {state.result.status === "confirmed"
              ? "Your appointment is confirmed."
              : "Your request has been sent."}
          </p>
          <p style={{ margin: "8px 0 0", color: "var(--nl-ink-dim)", fontSize: "0.9rem" }}>
            {describeBooking(state)}
          </p>

          {/* ── The way out ──────────────────────────────────────────────────
              Without this the confirmation is a DEAD END: the only way to book
              again is to reload the page. Somebody booking a cut for
              themselves and a colour for their daughter hits it immediately.

              The `restart` event already existed in the reducer, unused and
              untested — it was the button that was missing, not the mechanism.
              Found by auditing this surface on 2026-09-13 at Drea's request.

              It carries their details forward, so they do not retype a name
              and number they have just given. */}
          <button
            type="button"
            data-testid="journey-again"
            onClick={() => dispatch({ type: "restart" })}
            style={{
              ...primaryButton, background: "transparent", color: "var(--nl-ink-dim)",
              border: "1px solid var(--nl-stroke)", width: "auto", marginTop: 14,
            }}
          >Book another appointment</button>
        </section>
      )}

      {/* Back, never during a submit — see the reducer. */}
      {state.step !== "service" && state.step !== "done" && (
        <button
          type="button"
          data-testid="journey-back"
          disabled={state.submitting}
          onClick={() => dispatch({ type: "back" })}
          style={{
            ...primaryButton, background: "transparent", color: "var(--nl-ink-dim)",
            border: "1px solid var(--nl-stroke)", width: "auto", alignSelf: "flex-start",
          }}
        >Back</button>
      )}
    </div>
  );
}

function DetailsStep({ initial, onDone }: {
  initial: CustomerDetails | null;
  onDone: (details: CustomerDetails) => void;
}) {
  const [draft, setDraft] = useReducer(
    (prev: CustomerDetails, patch: Partial<CustomerDetails>) => ({ ...prev, ...patch }),
    initial ?? { fullName: "", phone: "", email: "", notes: "" },
  );
  const ready = detailsComplete(draft);

  const field: React.CSSProperties = {
    width: "100%", minHeight: "var(--nl-hit-min)",
    borderRadius: "var(--nl-radius-control)",
    border: "1px solid var(--nl-stroke)",
    background: "var(--nl-surface-raised)",
    color: "var(--nl-ink)", padding: "0 12px", font: "inherit",
  };

  return (
    <section style={panel} aria-label="Your details">
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {initial?.personId && (
          // A returning customer. Their details are filled in, but they still
          // see and confirm them — reuse is a convenience, not consent.
          <p style={{ margin: 0, fontSize: "0.82rem", color: "var(--nl-ink-dim)" }}>
            Welcome back. Check your details are still right.
          </p>
        )}
        <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: "0.8rem", color: "var(--nl-ink-dim)" }}>
          Name
          <input
            value={draft.fullName}
            onChange={e => setDraft({ fullName: e.target.value })}
            style={field}
            autoComplete="name"
          />
        </label>
        <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: "0.8rem", color: "var(--nl-ink-dim)" }}>
          Mobile
          <input
            value={draft.phone}
            onChange={e => setDraft({ phone: e.target.value })}
            style={field}
            inputMode="tel"
            autoComplete="tel"
          />
        </label>
        <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: "0.8rem", color: "var(--nl-ink-dim)" }}>
          Email (optional)
          <input
            value={draft.email ?? ""}
            onChange={e => setDraft({ email: e.target.value })}
            style={field}
            inputMode="email"
            autoComplete="email"
          />
        </label>
        <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: "0.8rem", color: "var(--nl-ink-dim)" }}>
          Anything we should know? (optional)
          <textarea
            value={draft.notes ?? ""}
            onChange={e => setDraft({ notes: e.target.value })}
            style={{ ...field, minHeight: "4rem", padding: "8px 12px", resize: "vertical" }}
          />
        </label>

        <button
          type="button"
          data-testid="details-continue"
          disabled={!ready}
          onClick={() => onDone(draft)}
          style={{ ...primaryButton, opacity: ready ? 1 : 0.5, cursor: ready ? "pointer" : "not-allowed" }}
        >Continue</button>

        {!ready && (
          // Says what is missing rather than just refusing. A disabled button
          // with no explanation is a dead end.
          <p style={{ margin: 0, fontSize: "0.78rem", color: "var(--nl-ink-faint)" }}>
            A name and a mobile number are needed so we can confirm your appointment.
          </p>
        )}
      </div>
    </section>
  );
}
